import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import AdSlot from "../components/AdSlot";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function MyPredictions() {
  const [preds, setPreds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/predictions/me");
        setPreds(data.predictions);
      } finally { setLoading(false); }
    })();
  }, []);

  const totalPoints = preds.reduce((s, p) => s + (p.points || 0), 0);
  const exactCount = preds.filter((p) => p.is_exact).length;
  const scoredCount = preds.filter((p) => p.scored).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <span className="text-xs uppercase tracking-[0.25em] text-primary-yellow font-bold">Your Form</span>
      <h1 className="font-heading text-5xl uppercase font-black mt-3">My Predictions</h1>

      <div className="mt-8 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
        <Stat label="Total Points" value={totalPoints} />
        <Stat label="Predictions Scored" value={scoredCount} />
        <Stat label="Exact Scores" value={exactCount} />
      </div>

      <div className="mt-8"><AdSlot id="picks-top" size="leaderboard" /></div>

      <div className="mt-10 border border-white/10">
        {loading && <div className="p-8 text-white/50 uppercase tracking-widest text-sm">Loading…</div>}
        {!loading && preds.length === 0 && (
          <div data-testid="predictions-empty" className="p-12 text-center text-white/60">
            No predictions yet. Head to <span className="text-primary-yellow">Fixtures</span> to make your picks.
          </div>
        )}
        {preds.map((p) => {
          const f = p.fixture;
          if (!f) return null;
          const finished = f.status === "finished";
          return (
            <div key={p.id} data-testid={`mypred-${p.id}`} className="grid grid-cols-12 gap-3 items-center px-4 sm:px-6 py-5 border-b border-white/5 last:border-b-0 surface">
              <div className="col-span-12 md:col-span-3">
                <div className="text-[10px] uppercase tracking-widest text-white/40">MW {f.matchweek}</div>
                <div className="text-sm text-white/70 font-mono-num">{fmtDate(f.kickoff)}</div>
              </div>
              <div className="col-span-12 md:col-span-5">
                <div className="font-heading uppercase font-bold">
                  {f.home_team} <span className="text-white/30 mx-2">vs</span> {f.away_team}
                </div>
              </div>
              <div className="col-span-6 md:col-span-2 text-right md:text-center">
                <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center justify-end md:justify-center gap-1.5">
                  Your pick
                  {p.default && (
                    <span
                      data-testid={`pred-default-${p.id}`}
                      className="text-[8px] tracking-widest text-white/40 border border-white/15 px-1 py-0.5"
                      title="You didn't submit a pick before kickoff — auto-scored as 0-0 per the default rule."
                    >
                      AUTO
                    </span>
                  )}
                </div>
                <div className="font-mono-num text-2xl font-black">{p.home_score}-{p.away_score}</div>
              </div>
              <div className="col-span-6 md:col-span-2 text-right">
                {finished ? (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-white/40">Actual {f.home_score}-{f.away_score}</div>
                    <div className={`font-mono-num text-3xl font-black ${p.points > 0 ? "text-primary-yellow" : "text-white/30"}`}>+{p.points || 0}</div>
                  </>
                ) : (
                  <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/50 border border-white/10 px-3 py-2">Pending</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="surface p-6">
    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
    <div className="font-heading text-5xl font-black text-primary-yellow font-mono-num mt-2">{value}</div>
  </div>
);
